import { CloseRounded as CloseIcon } from "@mui/icons-material";
import { Box, Drawer, Typography, Divider, Stack } from "@mui/material";
import * as dates from "common/dates";
import * as numbers from "common/numbers";
import CopyButton from "components/common/CopyButton";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import IconButton from "components/common/IconButton";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useSidebar } from "store/hooks/layoutHooks";

const nilCalculations = {
  totalDays: 0,
  daysPassed: 0,
  daysRemaining: 0,
  earnedFactor: 0,
  unearnedFactor: 0,
  earnedPremium: 0,
  unearnedPremium: 0,
};

const ProRataCalculator = ({ inception, expiry, premium, isOpen, onClose }) => {
  const { top } = useSidebar(isOpen);

  const getDefaultCancelDate = useCallback(() => {
    const today = dates.getDate().toISOString().substring(0, 10);
    return dates.strDateInRange(today, inception, expiry) ? today : null;
  }, [inception, expiry]);

  const [cancelDate, setCancelDate] = useState(getDefaultCancelDate());
  const [calculations, setCalculations] = useState(nilCalculations);

  useEffect(() => {
    setCancelDate(getDefaultCancelDate());
  }, [getDefaultCancelDate]);

  useEffect(() => {
    const calculations = { ...nilCalculations };

    if (inception && expiry) {
      const inceptionDate = dayjs(inception);
      const expiryDate = dayjs(expiry);
      calculations.totalDays = expiryDate.diff(inceptionDate, "day");

      if (cancelDate) {
        calculations.daysPassed = Math.max(
          0,
          Math.min(
            dayjs(cancelDate).diff(inceptionDate, "day") + 1,
            calculations.totalDays
          )
        );
        calculations.daysRemaining =
          calculations.totalDays - calculations.daysPassed;
        calculations.earnedFactor =
          calculations.totalDays > 0
            ? calculations.daysPassed / calculations.totalDays
            : 0;
        calculations.unearnedFactor = 1 - calculations.earnedFactor;

        calculations.earnedPremium = (premium ?? 0) * calculations.earnedFactor;
        calculations.unearnedPremium =
          (premium ?? 0) * calculations.unearnedFactor;
      }
    }

    setCalculations(calculations);
  }, [inception, expiry, premium, cancelDate]);

  const percentageFormat = numbers.percentage(2);

  return (
    <Drawer
      open={isOpen}
      variant={"persistent"}
      anchor={"right"}
      onClose={onClose}
      PaperProps={{
        className: "sidebar",
        sx: {
          top: `${top}px`,
          height: `calc(100vh - ${top}px)`,
          width: 400,
        },
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{ padding: "0.5rem 0.5rem 0.5rem 1rem" }}
      >
        <Typography variant={"h6"}>{"Pro Rata Calculator"}</Typography>
        <IconButton icon={CloseIcon} className={"link"} onClick={onClose} />
      </Stack>

      <Box sx={{ padding: "1rem", overflow: "auto", flex: 1 }}>
        <Stack spacing={3}>
          <Stack spacing={2}>
            <Typography variant={"subtitle2"}>
              {"Policy Information"}
            </Typography>

            <Box>
              <Typography variant={"body2"} color={"text.secondary"}>
                {"Total Premium"}
              </Typography>
              <Typography variant={"body1"}>
                {numbers.currencyFormat(premium || 0)}
              </Typography>
            </Box>

            <Box>
              <Typography variant={"body2"} color={"text.secondary"}>
                {"Inception Date"}
              </Typography>
              <Typography variant={"body1"}>
                {dayjs(inception).format("YYYY-MM-DD")}
              </Typography>
            </Box>

            <Box>
              <Typography variant={"body2"} color={"text.secondary"}>
                {"Expiry Date"}
              </Typography>
              <Typography variant={"body1"}>
                {dayjs(expiry).format("YYYY-MM-DD")}
              </Typography>
            </Box>

            <FormattedDatePicker
              label={"Cancellation Date"}
              value={cancelDate}
              onChangeDate={setCancelDate}
              compact={true}
            />
          </Stack>

          <Divider />

          <Stack gap={1}>
            <Typography variant={"subtitle2"}>{"Policy Period"}</Typography>

            <Box>
              <Typography variant={"body2"} color={"text.secondary"}>
                {"Total Policy Days"}
              </Typography>
              <Typography variant={"body1"}>
                {calculations.totalDays}
              </Typography>
            </Box>

            <Box>
              <Typography variant={"body2"} color={"text.secondary"}>
                {"Days Passed"}
              </Typography>
              <Typography variant={"body1"}>
                {calculations.daysPassed}
              </Typography>
            </Box>

            <Box>
              <Typography variant={"body2"} color={"text.secondary"}>
                {"Days Remaining"}
              </Typography>
              <Typography variant={"body1"}>
                {calculations.daysRemaining}
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Stack gap={1}>
            <Typography variant={"subtitle2"}>{"Factors"}</Typography>

            <Box>
              <Typography variant={"body2"} color={"text.secondary"}>
                {"Earned Factor"}
              </Typography>
              <Typography
                variant={"body1"}
                color={"success.main"}
                fontWeight={"medium"}
              >
                {percentageFormat(calculations.earnedFactor)}
              </Typography>
            </Box>

            <Box>
              <Typography variant={"body2"} color={"text.secondary"}>
                {"Unearned Factor"}
              </Typography>
              <Typography
                variant={"body1"}
                color={"warning.main"}
                fontWeight={"medium"}
              >
                {percentageFormat(calculations.unearnedFactor)}
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Stack gap={1}>
            <Typography variant={"subtitle2"}>{"Premium Breakdown"}</Typography>

            <Box>
              <Typography variant={"body2"} color={"text.secondary"}>
                {"Earned Premium"}
              </Typography>
              <Typography
                variant={"body1"}
                color={"success.main"}
                fontWeight={"medium"}
              >
                {numbers.currencyFormat(calculations.earnedPremium)}
              </Typography>
            </Box>

            <Box>
              <Typography variant={"body2"} color={"text.secondary"}>
                {"Unearned Premium"}
              </Typography>
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Typography
                  variant={"body1"}
                  color={"warning.main"}
                  fontWeight={"medium"}
                >
                  {numbers.currencyFormat(calculations.unearnedPremium)}
                </Typography>
                <CopyButton
                  content={calculations.unearnedPremium.toFixed(2)}
                  tooltip={"Copy value"}
                  scale={"small"}
                />
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default ProRataCalculator;
